// Store metadata is the only source for native subscription prices and periods.
export const offerKey = (p) => JSON.stringify([p?.planIdentifier || p?.productIdentifier || p?.identifier, p?.identifier, p?.offerToken || ""]);

export function periodLabel(period) {
  let count, unit;
  if (typeof period === "string") {
    const match = /^P([1-9]\d*)([DWMY])$/.exec(period);
    if (!match) return "";
    count = Number(match[1]); unit = { D: 0, W: 1, M: 2, Y: 3 }[match[2]];
  } else { count = period?.numberOfUnits; unit = period?.unit; }
  if (!Number.isInteger(count) || count < 1 || ![0, 1, 2, 3].includes(unit)) return "";
  const words = [count > 1 ? "jours" : "jour", count > 1 ? "semaines" : "semaine", "mois", count > 1 ? "ans" : "an"];
  return `${count} ${words[unit]}`;
}

export function offerTerms(p) {
  if (!p?.priceString) return null;
  if (p.planIdentifier) {
    const phases = p.pricingPhases;
    // Prepaid and installment plans require dedicated presentation and flows.
    if (p.installmentPlan || !p.offerToken || !phases?.length || phases[phases.length - 1].recurrenceMode !== 1) return null;
    if (phases.some((s, i) => !s.formattedPrice || !periodLabel(s.billingPeriod) ||
      !Number.isSafeInteger(s.priceAmountMicros) || s.priceAmountMicros < 0 ||
      !s.priceCurrencyCode || (i < phases.length - 1 &&
        !((s.recurrenceMode === 2 && Number.isInteger(s.billingCycleCount) && s.billingCycleCount > 0) || s.recurrenceMode === 3)))) return null;
    const regular = phases[phases.length - 1];
    return {
      price: regular.formattedPrice, period: periodLabel(regular.billingPeriod),
      description: phases.map((s, i) => `${s.formattedPrice} / ${periodLabel(s.billingPeriod)}${i < phases.length - 1 ? ` pendant ${s.recurrenceMode === 3 ? 1 : s.billingCycleCount} période(s)` : ", renouvellement automatique"}`).join(", puis "),
    };
  }
  const period = periodLabel(p.subscriptionPeriod);
  if (!period) return null;
  let intro = "";
  if (p.isEligibleForIntroOffer === true && p.introductoryPrice) {
    const d = p.introductoryPrice;
    const duration = periodLabel(d.subscriptionPeriod);
    if (!duration || !d.priceString || !Number.isInteger(d.numberOfPeriods) || d.numberOfPeriods < 1) return null;
    if (![0, 1, 2].includes(d.paymentMode)) return null;
    intro = d.paymentMode === 2
      ? `${d.priceString} / ${duration} pendant ${d.numberOfPeriods} période(s), puis `
      : `${d.priceString} pour ${periodLabel({ ...d.subscriptionPeriod, numberOfUnits: d.subscriptionPeriod.numberOfUnits * d.numberOfPeriods })}, puis `;
  }
  return { price: p.priceString, period, description: `${intro}${p.priceString} / ${period}, renouvellement automatique` };
}

export function groupOffers(list) {
  const grouped = {};
  for (const p of list) {
    if (!offerTerms(p)) continue;
    const id = p.planIdentifier || p.productIdentifier || p.identifier;
    (grouped[id] ||= []).push(p);
  }
  // Prefer a standard base plan; never silently overwrite it with a promotion.
  Object.values(grouped).forEach((offers) => offers.sort((a, b) => Number(!!a.offerId) - Number(!!b.offerId) || offerKey(a).localeCompare(offerKey(b))));
  return grouped;
}

export function selectedOffer(offers, key) {
  return key ? offers?.find((p) => offerKey(p) === key) : offers?.[0];
}

export function offerFingerprint(p) {
  // Native dictionaries have no stable key order. Normalize object keys while
  // preserving array order (especially the sequence of Google pricing phases).
  const canonicalize = (_, value) => value && typeof value === "object" && !Array.isArray(value)
    ? Object.fromEntries(Object.keys(value).sort().map((key) => [key, value[key]]))
    : value;
  return JSON.stringify([offerKey(p), p?.priceString, p?.price, p?.currencyCode, p?.subscriptionPeriod,
    p?.pricingPhases, p?.installmentPlan, p?.isEligibleForIntroOffer, p?.introductoryPrice], canonicalize);
}

export function googleReplacementMode(source, target, catalog, upgrading) {
  if (!source?.auto_renewing || source.installment || !offerTerms(target)) {
    throw new Error("Ce changement nécessite la gestion des abonnements Google Play.");
  }
  const old = catalog.find((p) => p.planIdentifier === source.product_id && p.identifier === source.base_plan_id && !p.offerId);
  const oldPhase = old?.pricingPhases?.[old.pricingPhases.length - 1];
  const next = target.pricingPhases[target.pricingPhases.length - 1];
  // Compare catalog prices on the same tax/display basis. Google alone computes
  // the remaining paid value (which may include a grandfathered price/discount).
  // WITH_TIME_PRORATION never requires us to infer a tax-inclusive charge from
  // the server's tax-exclusive recurringPrice.
  return upgrading && target.pricingPhases.length === 1 && oldPhase?.billingPeriod === next.billingPeriod &&
    oldPhase?.priceCurrencyCode === next.priceCurrencyCode && Number.isSafeInteger(oldPhase.priceAmountMicros) &&
    oldPhase.priceAmountMicros > 0 && next.priceAmountMicros > oldPhase.priceAmountMicros
    ? "WITH_TIME_PRORATION" : "DEFERRED";
}
